import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";
import { formatViews, getCategoryColor } from "@/utils/format";

interface NewsCardProps {
  item: NewsItem;
  imageClassName?: string;
  horizontal?: boolean;
}

export default function NewsCard({ item, horizontal = false }: NewsCardProps) {
  if (horizontal) {
    return (
      <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200">
        <Link
          to={`/news/${item.slug}`}
          className="relative block h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-44"
          aria-label={item.title}
        >
          <img
            src={item.cover}
            alt={item.alt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {item.exclusive && (
            <span className="absolute left-1.5 top-1.5 rounded bg-primary-500 px-1.5 py-0.5 text-[10px] font-bold text-background-50">
              独家
            </span>
          )}
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-center py-1">
          <Link to={`/news/${item.slug}`} className="line-clamp-2 text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
            {item.title}
          </Link>
          <p className="mt-2 hidden text-xs leading-relaxed text-foreground-500 sm:line-clamp-2">
            {item.summary}
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
            <span className="whitespace-nowrap">{item.date}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-fire-line"></i>{formatViews(item.views)}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-chat-3-line"></i>{item.comments}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
      <Link to={`/news/${item.slug}`} className="relative block aspect-[16/10] w-full overflow-hidden" aria-label={item.title}>
        <img
          src={item.cover}
          alt={item.alt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {item.exclusive && (
          <span className="absolute left-3 top-3 rounded bg-primary-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
            独家首发
          </span>
        )}
        {item.hot && (
          <span className="absolute right-3 top-3 rounded bg-accent-500 px-2 py-0.5 text-[11px] font-bold text-background-50">
            爆
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${getCategoryColor(item.categorySlug)}`}>
            {item.category}
          </span>
          <span className="text-xs text-foreground-400">{item.tag}</span>
        </div>
        <Link to={`/news/${item.slug}`} className="mt-3 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {item.title}
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
          {item.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-400">
          <span className="whitespace-nowrap">{item.date}</span>
          <span className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(item.views)}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-chat-3-line"></i>{item.comments}</span>
          </span>
        </div>
      </div>
    </article>
  );
}